import * as React from 'react';
import { useSnackbar } from 'notistack';
import axios from '../../utils/axios';
import { Num } from './index';
import Modal, { BotaoSecundario, Campo, classesEntrada } from './Modal';
import { cn } from '../../lib/utils';
import { AutoCompleteMultiplo, buscarPaginado } from './AutoComplete';

// Filtros fixos - ver o cabecalho abaixo.
const buscarGrupos = buscarPaginado(
  (termo, pagina, linhas) =>
    `/entidades/paginado?page=${pagina}&rows=${linhas}&campo=nome&valor=${encodeURIComponent(termo)}` +
    `&direcao=asc&ordenar=nome&status=1&minhasregionais=true&somenteComPoliticas=true`,
  (g) => ({ codigo: g.codigo, rotulo: g.nome })
);

// Mesmo teto da API (aplicativos.service.ts) - ver o seletor de grupos.
const MAX_GRUPOS_POR_VEZ = 10;

/* ============================================================================
   Limpar DADOS de um aplicativo em varios grupos.

   O NOME "CACHE" SAIU DA TELA (28/09/2026). O comando enviado ao Google e
   CLEAR_APP_DATA (ver google/util/clear-cache-device.ts), que apaga TODOS os
   dados do aplicativo - login, configuracao, banco local -, e nao so o cache.
   O corpo do modal ja dizia a verdade ("deixa o aplicativo como recem-instalado"),
   mas o titulo e o botao diziam "cache", que e a acao inofensiva que a pessoa
   acha que esta pedindo. Rotulo que subestima o estrago e o pior tipo de rotulo
   errado: quem lê "limpar cache" clica sem pensar duas vezes.

   O nome do ARQUIVO e do componente ficaram como estao - renomear mexe em
   import de outras telas sem mudar nada para quem usa o sistema.

   Substitui pages/dashboard/aplicativos/components/modal/modal-limpar-cache.tsx
   (Dialog do MUI + RHFAutoComplete).

   Contrato, o mesmo de antes:
     GET  /entidades/paginado?...&minhasregionais=true&somenteComPoliticas=true
     POST /aplicativos/limparCacheGrupo { grupos, aplicativo, listCampoData }

   OS DOIS PARAMETROS DA BUSCA DE GRUPO NAO SAO ENFEITE, e por isso estao fixos:
     `minhasregionais=true`    - so os grupos das regionais do usuario;
     `somenteComPoliticas=true` - so os grupos QUE TEM POLITICA. Limpar cache
        atua pela politica; num grupo sem politica a acao nao tem por onde chegar
        ao aparelho, entao ele nem deve aparecer na lista.

   `listCampoData` e a trilha de auditoria (uma entrada por grupo). Sem ela a
   acao acontece sem registro de quem mandou e em quais grupos.

   TRES MUDANCAS DE COMPORTAMENTO:

   1. NAO NAVEGA MAIS AO TERMINAR. O modal antigo fazia
      `push(PATH_DASHBOARD.entidades.dispositivos(response?.data?.entidade?.codigo))`
      depois de salvar - e `response.data.entidade` nao existe no retorno deste
      endpoint, entao o push ia para `/dispositivos/undefined`. Agora fecha e
      pronto.

   2. CONFIRMACAO ANTES DE EXECUTAR. Limpar cache derruba dados locais do app em
      todos os aparelhos dos grupos escolhidos - um clique sem pergunta e muito
      alcance para pouca cerimonia.

   3. A lista mostra QUANTOS GRUPOS foram escolhidos e quais, no proprio botao e
      na confirmacao.
   ============================================================================ */

type Grupo = { codigo: number; nome: string };

export default function ModalLimparCache({
  aberto,
  aplicativo,
  onFechar,
  onExecutado,
}: {
  aberto: boolean;
  aplicativo: { name?: string; packageName?: string } | null;
  onFechar: () => void;
  onExecutado?: () => void;
}) {
  const { enqueueSnackbar } = useSnackbar();

  const [selecionados, setSelecionados] = React.useState<Grupo[]>([]);
  const [confirmando, setConfirmando] = React.useState(false);
  const [executando, setExecutando] = React.useState(false);

  React.useEffect(() => {
    if (!aberto) return;
    setSelecionados([]);
    setConfirmando(false);
  }, [aberto, aplicativo?.packageName]);

  async function executar() {
    if (!selecionados.length || executando) return;
    setExecutando(true);
    try {
      // Uma entrada de auditoria por grupo - mesmo formato do modal antigo.
      const listCampoData = selecionados.map((g) => ({
        campo: `aplicativo: ${aplicativo?.name}`,
        antigo: ' - ',
        novo: `grupo: ${g.nome}`,
      }));

      await axios.post('/aplicativos/limparCacheGrupo', {
        listCampoData: listCampoData.length ? JSON.stringify(listCampoData) : undefined,
        grupos: selecionados,
        aplicativo,
      });

      // O Google ENFILEIRA o comando: aparelho offline so recebe quando voltar a ficar online.
      enqueueSnackbar('Comando enviado. Os dados são apagados em cada aparelho quando ele estiver online.', { variant: 'success' });
      setConfirmando(false);
      onExecutado?.();
      onFechar();
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || 'erro no sistema', {
        variant: 'error',
        style: { whiteSpace: 'pre-line' },
      });
    } finally {
      setExecutando(false);
    }
  }

  return (
    <>
      <Modal
        aberto={aberto && !confirmando}
        titulo="Limpar dados do aplicativo em vários grupos"
        descricao={aplicativo ? `${aplicativo.name ?? ''} · ${aplicativo.packageName ?? ''}` : undefined}
        icone="🧹"
        largura="max-w-[560px]"
        onFechar={onFechar}
        rodape={
          <>
            <BotaoSecundario onClick={onFechar} disabled={executando}>
              Cancelar
            </BotaoSecundario>
            <button
              type="button"
              onClick={() => setConfirmando(true)}
              disabled={!selecionados.length || executando}
              className="h-9 rounded-lg bg-danger px-4 text-[12.5px] font-bold text-white disabled:opacity-40"
            >
              Limpar dados ({selecionados.length})
            </button>
          </>
        }
      >
        <Campo
          bloco
          rotulo="Grupos"
          obrigatorio
          dica="Até 10 grupos por vez. Só aparecem grupos das suas regionais que tenham política — sem política, a ação não chega ao aparelho."
        >
          {/* Lista paginada no servidor, so ao clicar na lupa (21/10/2026), com os
              mesmos filtros fixos de antes. Ate 10 por vez (29/09/2026): cada grupo
              regrava a politica no Google, em serie; a API recusa acima disso. */}
          <AutoCompleteMultiplo
            selecionados={selecionados.map((g) => ({ codigo: g.codigo, rotulo: g.nome }))}
            aoMudar={(l) => setSelecionados(l.map((o) => ({ codigo: Number(o.codigo), nome: o.rotulo })))}
            buscar={buscarGrupos}
            maximo={MAX_GRUPOS_POR_VEZ}
            titulo="Selecionar grupos"
            placeholder="Nome do grupo (Enter ou lupa)"
            vazio="Nenhum grupo com política encontrado."
          />
        </Campo>

        <div className="rounded-lg border border-line bg-surface-2 p-3 text-[12px] text-muted-foreground">
          Apaga <strong>todos os dados</strong> deste aplicativo nos aparelhos dos grupos escolhidos —
          login, configuração e banco local. O app volta ao estado de recém-instalado. Não é só cache, e
          não há como desfazer.
        </div>
      </Modal>

      <Modal
        aberto={confirmando}
        titulo="Limpar dados agora?"
        descricao="A ação vale para todos os aparelhos dos grupos selecionados."
        icone="!"
        largura="max-w-[480px]"
        onFechar={() => setConfirmando(false)}
        rodape={
          <>
            <BotaoSecundario onClick={() => setConfirmando(false)} disabled={executando}>
              Voltar
            </BotaoSecundario>
            <button
              type="button"
              onClick={executar}
              disabled={executando}
              className="h-9 rounded-lg bg-danger px-4 text-[12.5px] font-bold text-white disabled:opacity-40"
            >
              {executando ? 'Executando...' : 'Limpar dados'}
            </button>
          </>
        }
      >
        <div className="rounded-lg border border-line bg-surface-2 p-3 text-[12px]">
          <span className="block font-semibold">{aplicativo?.name || '—'}</span>
          <Num className="block text-[11px] text-muted-foreground">{aplicativo?.packageName || '—'}</Num>
          <span className="mt-2 block text-muted-foreground">
            <strong className="text-ink">{selecionados.length}</strong> grupo(s):{' '}
            {selecionados.map((g) => g.nome).join(', ')}
          </span>
        </div>
      </Modal>
    </>
  );
}
